(function () {
  if (customElements.get('bundle-purchase')) return;

  const escMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => escMap[c]);

  const formatMoney = (cents) => {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat || '${{amount}}');
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, '${{amount}}');
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  };

  const fillPlaceholders = (template, vars) => {
    if (!template) return '';
    return String(template).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  };

  class BundlePurchase extends HTMLElement {
    connectedCallback() {
      const cfgScript = this.querySelector('[data-bundle-config]');
      if (!cfgScript) return;
      try {
        this.config = JSON.parse(cfgScript.textContent);
      } catch (err) {
        console.error('[bundle-purchase] config parse error', err);
        return;
      }
      if (!this.config.tiers || !this.config.tiers.length) return;

      this.root = this.querySelector('[data-bundle-root]');
      this.host = this.closest('product-info') || this.closest('[data-section-id]') || document;
      this.form = this.host.querySelector('form[data-product-form]');
      this.qtyInput = this.host.querySelector('[data-quantity-field]');

      const colorPos = (this.config.colorOptionPosition || 1) - 1;
      this.state = {
        nativeColor: (this.config.currentVariantOptions && this.config.currentVariantOptions[colorPos]) || '',
        quantity: this.readQty(),
        allocations: {},
        allocationNotice: null,
        openTier: this.config.tiers[0].quantity,
        selectedTier: null,
        selectedGiftId: null,
      };
      this.syncNativeColor();
      this.ensureAllocation(this.state.openTier, false);

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onQtyChange = this.onQtyChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('input', this.onInput);
      if (this.qtyInput) this.qtyInput.addEventListener('change', this.onQtyChange);
      if (this.form) this.form.addEventListener('change', this.onVariantChange);
      if (this.form) this.form.addEventListener('submit', this.onSubmit, true);

      this.render();
    }

    readQty() {
      const n = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    currentVariant() {
      const input = this.form && this.form.querySelector('[name="id"]');
      const id = input ? Number(input.value) : this.config.currentVariantId;
      return this.config.variants.find((v) => v.id === id) || null;
    }

    currentVariantPrice() {
      const variant = this.currentVariant();
      return (variant && variant.price) || this.config.currentVariantPrice || 0;
    }

    syncNativeColor() {
      const variant = this.currentVariant();
      const colorPos = (this.config.colorOptionPosition || 1) - 1;
      if (variant && variant.options && variant.options[colorPos]) {
        this.state.nativeColor = variant.options[colorPos];
      }
    }

    ensureAllocation(tierQty, prefill = true) {
      if (!this.state.allocations[tierQty]) {
        const a = {};
        for (const c of this.config.colors) {
          a[c.name] = prefill && c.name === this.state.nativeColor ? tierQty : 0;
        }
        this.state.allocations[tierQty] = a;
      }
    }

    completeAllocation(tierQty) {
      this.ensureAllocation(tierQty, false);
      const allocation = this.state.allocations[tierQty];
      const used = this.allocationTotal(tierQty);
      if (used < tierQty) {
        allocation[this.state.nativeColor] = (allocation[this.state.nativeColor] || 0) + (tierQty - used);
      }
    }

    allocationTotal(tierQty) {
      const a = this.state.allocations[tierQty] || {};
      return Object.values(a).reduce((s, v) => s + (Number(v) || 0), 0);
    }

    setAllocationValue(tierQty, color, value) {
      this.ensureAllocation(tierQty, false);
      const allocation = this.state.allocations[tierQty];
      const current = Number(allocation[color] || 0);
      const usedByOthers = this.allocationTotal(tierQty) - current;
      const maxForColor = Math.max(0, tierQty - usedByOthers);
      const next = Math.max(0, Number.isFinite(value) ? value : 0);

      if (next > maxForColor) {
        allocation[color] = maxForColor;
        this.state.allocationNotice = { tierQty, message: 'Quantity limit exceeded.' };
      } else {
        allocation[color] = next;
        if (this.state.allocationNotice && this.state.allocationNotice.tierQty === tierQty) {
          this.state.allocationNotice = null;
        }
      }

      return allocation[color];
    }

    refreshAllocationUi(tierQty, activeInput) {
      const detail = this.root.querySelector('[data-tier="' + tierQty + '"] [data-tier-detail]');
      if (!detail) return;

      const used = this.allocationTotal(tierQty);
      const remaining = Math.max(0, tierQty - used);
      const totalEl = detail.querySelector('.bundle-purchase__alloc-total');
      if (totalEl) {
        const selectedEl = totalEl.querySelector('strong');
        if (selectedEl) selectedEl.textContent = used;
        const spans = totalEl.querySelectorAll('span');
        if (spans[1]) spans[1].textContent = remaining + ' remaining';
      }

      for (const input of detail.querySelectorAll('[data-alloc-input]')) {
        const color = input.dataset.color;
        const value = Number((this.state.allocations[tierQty] || {})[color] || 0);
        input.max = value + remaining;
        if (input !== activeInput) input.value = value;

        const row = input.closest('.bundle-purchase__alloc-row');
        if (!row) continue;
        const minus = row.querySelector('[data-alloc-minus]');
        const plus = row.querySelector('[data-alloc-plus]');
        if (minus) minus.disabled = value <= 0;
        if (plus) plus.disabled = remaining <= 0;
      }

      const existingNotice = detail.querySelector('.bundle-purchase__alloc-notice');
      const showNotice = this.state.allocationNotice && this.state.allocationNotice.tierQty === tierQty;
      if (showNotice && existingNotice) {
        existingNotice.textContent = this.state.allocationNotice.message;
      } else if (showNotice) {
        const notice = document.createElement('div');
        notice.className = 'bundle-purchase__alloc-notice';
        notice.setAttribute('role', 'status');
        notice.textContent = this.state.allocationNotice.message;
        totalEl && totalEl.insertAdjacentElement('afterend', notice);
      } else if (existingNotice) {
        existingNotice.remove();
      }

      const summary = this.root.querySelector('.bundle-purchase__summary');
      if (summary) summary.innerHTML = this.renderSummary();
      this.syncAtcButton();
    }

    activeTier() {
      return this.config.tiers.find((t) => t.quantity === this.state.selectedTier) || null;
    }

    openTier() {
      return this.config.tiers.find((t) => t.quantity === this.state.openTier) || this.config.tiers[0];
    }

    unit(n) {
      const s = this.config.unitSingular || 'pair';
      const p = this.config.unitPlural || 'pairs';
      return n === 1 ? s : p;
    }

    tierVars(tier) {
      const total = this.tierTotalPrice(tier);
      const compare = this.tierComparePrice(tier);
      const perItem = tier.quantity > 0 ? Math.round(total / tier.quantity) : total;
      const savings = Math.max(0, compare - total);
      return {
        per_item: formatMoney(perItem),
        savings: formatMoney(savings),
        tier_total: formatMoney(total),
        tier_compare: formatMoney(compare),
        quantity: String(tier.quantity),
        unit: this.unit(tier.quantity),
      };
    }

    tierUnitPrice(tier) {
      const factor = 1 - (Number(tier.discountPercent) || 0) / 100;
      return Math.round(this.currentVariantPrice() * factor);
    }

    tierTotalPrice(tier) {
      return this.tierUnitPrice(tier) * tier.quantity;
    }

    tierComparePrice(tier) {
      return this.currentVariantPrice() * tier.quantity;
    }

    findVariant(colorName) {
      const current = this.currentVariant();
      const target = ((current && current.options) || this.config.currentVariantOptions || []).slice();
      target[(this.config.colorOptionPosition || 1) - 1] = colorName;
      return this.config.variants.find((v) => v.options.every((o, i) => o === target[i]));
    }

    onQtyChange() {
      this.state.quantity = this.readQty();
      this.syncNativeColor();
      this.render();
    }

    onVariantChange() {
      this.syncNativeColor();
      this.render();
    }

    onInput(e) {
      const allocInput = e.target.closest('[data-alloc-input]');
      if (!allocInput) return;

      const tierQty = Number(allocInput.dataset.tierQty);
      const color = allocInput.dataset.color;
      const raw = String(allocInput.value || '').replace(/[^\d]/g, '');
      const value = raw === '' ? 0 : parseInt(raw, 10);
      const corrected = this.setAllocationValue(tierQty, color, value);

      if (String(corrected) !== allocInput.value) allocInput.value = corrected;
      this.refreshAllocationUi(tierQty, allocInput);
    }

    onClick(e) {
      const allocPlus = e.target.closest('[data-alloc-plus]');
      const allocMinus = e.target.closest('[data-alloc-minus]');
      const giftBtn = e.target.closest('[data-gift]');
      const skipGift = e.target.closest('[data-skip-gift]');
      const tierEl = e.target.closest('[data-tier]');

      if (allocPlus || allocMinus) {
        e.preventDefault();
        const btn = allocPlus || allocMinus;
        const tierQty = Number(btn.dataset.tierQty);
        const color = btn.dataset.color;
        this.ensureAllocation(tierQty);
        const a = this.state.allocations[tierQty];
        const used = this.allocationTotal(tierQty);
        this.state.allocationNotice = null;
        if (allocPlus && used < tierQty) a[color] = (a[color] || 0) + 1;
        if (allocMinus && (a[color] || 0) > 0) a[color] = a[color] - 1;
        this.render();
        return;
      }

      if (skipGift) {
        e.preventDefault();
        this.state.selectedGiftId = null;
        this.render();
        return;
      }

      if (giftBtn) {
        e.preventDefault();
        const id = Number(giftBtn.dataset.gift);
        this.state.selectedGiftId = this.state.selectedGiftId === id ? null : id;
        this.render();
        return;
      }

      if (tierEl && !e.target.closest('[data-tier-detail]')) {
        e.preventDefault();
        const q = Number(tierEl.dataset.tier);
        const wasSelected = this.state.selectedTier === q;
        this.state.openTier = q;
        this.state.allocationNotice = null;
        this.syncNativeColor();
        if (wasSelected) {
          this.state.selectedTier = null;
        } else {
          this.state.selectedTier = q;
          this.state.quantity = q;
          if (this.qtyInput) {
            this.qtyInput.value = q;
            this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.completeAllocation(q);
        }
        this.render();
      }
    }

    findActiveGift(tier) {
      if (!tier.gifts || !tier.gifts.length) return null;
      return tier.gifts.find((g) => g.productId === this.state.selectedGiftId) || null;
    }

    async onSubmit(e) {
      const tier = this.activeTier();
      if (!tier) return;
      if (tier.quantity <= 1 && this.state.quantity <= 1) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const tierQty = tier.quantity;
      this.ensureAllocation(tierQty);
      const allocation = Object.assign({}, this.state.allocations[tierQty]);
      const extra = Math.max(0, this.state.quantity - tierQty);
      if (extra > 0) {
        allocation[this.state.nativeColor] = (allocation[this.state.nativeColor] || 0) + extra;
      }

      const items = [];
      for (const colorName of Object.keys(allocation)) {
        const qty = Number(allocation[colorName] || 0);
        if (qty <= 0) continue;
        const v = this.findVariant(colorName);
        if (!v) continue;
        items.push({
          id: v.id,
          quantity: qty,
          properties: { _qwe_bundle_tier: String(tierQty) },
        });
      }

      const gift = tier.hasGift ? this.findActiveGift(tier) : null;
      if (gift) {
        items.push({
          id: gift.variantId,
          quantity: 1,
          properties: { _qwe_gift_for_bundle: String(tierQty) },
        });
      }

      if (!items.length) return;

      const submitBtn = this.form.querySelector('[data-add-to-cart]');
      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        submitBtn.setAttribute('disabled', 'disabled');
      }

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const resp = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          console.error('[bundle-purchase] cart/add error', data);
          return;
        }
        const drawer = document.querySelector('#cart-drawer') || document.querySelector('cart-element[data-cart-drawer]');
        if (drawer && typeof drawer.getCart === 'function') drawer.getCart();
        if (drawer && typeof drawer.openCartDrawer === 'function') drawer.openCartDrawer();
      } catch (err) {
        console.error('[bundle-purchase] add to cart failed', err);
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.removeAttribute('disabled');
        }
      }
    }

    swatchHtml(color) {
      if (color.swatchImage) {
        return '<span class="bundle-purchase__swatch" style="background-image:url(\'' + esc(color.swatchImage) + '\')"></span>';
      }
      if (color.swatchColor) {
        return '<span class="bundle-purchase__swatch" style="background-color:' + esc(color.swatchColor) + '"></span>';
      }
      return '<span class="bundle-purchase__swatch"></span>';
    }

    renderTier(tier) {
      const isActive = tier.quantity === this.state.selectedTier;
      const isOpen = tier.quantity === this.state.openTier;
      const total = this.tierTotalPrice(tier);
      const compare = this.tierComparePrice(tier);
      const compareHtml = compare > total ? '<s>' + formatMoney(compare) + '</s>' : '';
      const detail = isOpen ? this.renderDetail(tier) : '';
      const vars = this.tierVars(tier);
      const title = fillPlaceholders(tier.title, vars);
      const pill = fillPlaceholders(tier.pillText, vars);
      const save = fillPlaceholders(tier.saveText, vars);
      const badge = fillPlaceholders(tier.badge, vars);

      return (
        '<article class="bundle-purchase__tier' + (isActive ? ' is-selected' : '') + (isOpen ? ' is-open' : '') + '" data-tier="' + tier.quantity + '">' +
          '<span class="bundle-purchase__radio"></span>' +
          '<div>' +
            '<div class="bundle-purchase__tier-title">' +
              '<span>' + esc(title) + '</span>' +
              (pill ? '<span class="bundle-purchase__pill">' + esc(pill) + '</span>' : '') +
              (badge ? '<span class="bundle-purchase__badge">' + esc(badge) + '</span>' : '') +
            '</div>' +
            (save ? '<div class="bundle-purchase__tier-copy">' + esc(save) + '</div>' : '') +
          '</div>' +
          '<div class="bundle-purchase__price">' + formatMoney(total) + compareHtml + '</div>' +
          detail +
        '</article>'
      );
    }

    renderDetail(tier) {
      const tierQty = tier.quantity;
      const allocation = this.state.allocations[tierQty];
      const used = this.allocationTotal(tierQty);
      const remaining = Math.max(0, tierQty - used);
      const extra = Math.max(0, this.state.quantity - tierQty);

      const rows = this.config.colors.map((c) => {
        const v = Number(allocation[c.name] || 0);
        const max = v + remaining;
        const plusDis = remaining <= 0 ? 'disabled' : '';
        const minusDis = v <= 0 ? 'disabled' : '';
        return (
          '<div class="bundle-purchase__alloc-row">' +
            '<div class="bundle-purchase__alloc-color">' +
              this.swatchHtml(c) +
              '<span>' + esc(c.name) + '</span>' +
            '</div>' +
            '<div class="bundle-purchase__stepper">' +
              '<button type="button" data-alloc-minus data-tier-qty="' + tierQty + '" data-color="' + esc(c.name) + '" ' + minusDis + '>&minus;</button>' +
              '<input type="number" inputmode="numeric" pattern="[0-9]*" min="0" max="' + max + '" value="' + v + '" data-alloc-input data-tier-qty="' + tierQty + '" data-color="' + esc(c.name) + '" aria-label="' + esc(c.name) + ' quantity">' +
              '<button type="button" data-alloc-plus data-tier-qty="' + tierQty + '" data-color="' + esc(c.name) + '" ' + plusDis + '>+</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      const extraNote = extra > 0
        ? '<div class="bundle-purchase__extra-note">Your ' + extra + ' extra ' + this.unit(extra) + ' will be added in ' + esc(this.state.nativeColor) + '.</div>'
        : '';

      const giftPanel = (tier.hasGift && tier.gifts && tier.gifts.length)
        ? this.renderGiftPanel(tier)
        : '';
      const notice = (this.state.allocationNotice && this.state.allocationNotice.tierQty === tierQty)
        ? '<div class="bundle-purchase__alloc-notice" role="status">' + esc(this.state.allocationNotice.message) + '</div>'
        : '';

      return (
        '<div class="bundle-purchase__detail" data-tier-detail>' +
          '<div class="bundle-purchase__detail-head">' +
            '<strong>Choose color quantities</strong>' +
            '<span>Total for this tier: ' + tierQty + ' ' + this.unit(tierQty) + '</span>' +
          '</div>' +
          '<div class="bundle-purchase__alloc">' +
            rows +
            '<div class="bundle-purchase__alloc-total">' +
              '<span><strong>' + used + '</strong> of ' + tierQty + ' selected</span>' +
              '<span>' + remaining + ' remaining</span>' +
            '</div>' +
            notice +
          '</div>' +
          extraNote +
          giftPanel +
        '</div>'
      );
    }

    renderGiftPanel(tier) {
      const giftCards = (tier.gifts || []).map((g) => {
        const active = this.state.selectedGiftId === g.productId ? ' is-active' : '';
        const thumb = g.image
          ? '<span class="bundle-purchase__gift-thumb" style="background-image:url(\'' + esc(g.image) + '\')"></span>'
          : '<span class="bundle-purchase__gift-thumb"></span>';
        return (
          '<button type="button" class="bundle-purchase__gift-card' + active + '" data-gift="' + g.productId + '">' +
            thumb +
            '<span><strong>' + esc(g.title) + '</strong><span>Added at ' + formatMoney(0) + '</span></span>' +
            '<s>' + formatMoney(g.comparePrice) + '</s>' +
          '</button>'
        );
      }).join('');

      const skipActive = this.state.selectedGiftId === null ? ' is-active' : '';
      const skipThumb = this.config.skipGiftImage
        ? '<span class="bundle-purchase__gift-thumb" style="background-image:url(\'' + esc(this.config.skipGiftImage) + '\')"></span>'
        : '<span class="bundle-purchase__gift-thumb" style="background:#eee"></span>';
      const skipCard =
        '<button type="button" class="bundle-purchase__gift-card' + skipActive + '" data-skip-gift>' +
          skipThumb +
          '<span><strong>' + esc(this.config.skipGiftLabel) + '</strong><span>&nbsp;</span></span>' +
          '<s>&nbsp;</s>' +
        '</button>';

      return (
        '<div class="bundle-purchase__gift-panel">' +
          '<p class="bundle-purchase__gift-title">' + esc(this.config.giftSectionTitle) + '</p>' +
          '<div class="bundle-purchase__gift-row">' + giftCards + skipCard + '</div>' +
        '</div>'
      );
    }

    renderSummary() {
      const tier = this.activeTier();
      if (!tier) {
        const qty = this.state.quantity;
        const color = this.state.nativeColor ? ' ' + esc(this.state.nativeColor) : '';
        return '<strong>Cart preview:</strong> ' + qty + color + '.';
      }
      const tierQty = tier.quantity;
      const a = this.state.allocations[tierQty] || {};
      const parts = Object.keys(a).filter((c) => Number(a[c]) > 0).map((c) => a[c] + ' ' + c);
      const extra = Math.max(0, this.state.quantity - tierQty);
      const extraText = extra > 0 ? ' Extra: ' + extra + ' ' + this.unit(extra) + ' in ' + esc(this.state.nativeColor) + '.' : '';
      const text = parts.length ? parts.join(', ') : 'No colors selected yet.';
      return '<strong>Cart preview:</strong> ' + text + '.' + extraText;
    }

    syncAtcButton() {
      const tier = this.activeTier();
      if (!tier) {
        const total = this.currentVariantPrice() * this.state.quantity;
        const priceEl = this.host.querySelector('[data-add-to-cart] [data-product-price]');
        if (priceEl) priceEl.innerHTML = formatMoney(total);
        const compareEl = this.host.querySelector('[data-add-to-cart] [data-compare-price]');
        if (compareEl) compareEl.classList.add('hidden');
        return;
      }
      const total = this.tierTotalPrice(tier);
      const compare = this.tierComparePrice(tier);

      const priceEl = this.host.querySelector('[data-add-to-cart] [data-product-price]');
      if (priceEl) priceEl.innerHTML = formatMoney(total);

      const compareEl = this.host.querySelector('[data-add-to-cart] [data-compare-price]');
      if (compareEl) {
        if (compare > total) {
          compareEl.innerHTML = formatMoney(compare);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }
    }

    render() {
      const heading = this.config.heading
        ? '<div class="bundle-purchase__title">' + esc(this.config.heading) + '</div>'
        : '';
      const mixNote = this.config.mixMatchText
        ? '<div class="bundle-purchase__mix-note">' + this.config.mixMatchText + '</div>'
        : '';
      const tiersHtml = this.config.tiers.map((t) => this.renderTier(t)).join('');

      this.root.innerHTML =
        heading +
        mixNote +
        '<div class="bundle-purchase__tier-list">' + tiersHtml + '</div>' +
        '<div class="bundle-purchase__summary">' + this.renderSummary() + '</div>';

      this.syncAtcButton();
    }
  }

  customElements.define('bundle-purchase', BundlePurchase);
})();
